import { useReactiveVar } from '@apollo/client';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Box, Button, Chip, Skeleton } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { alpha, useTheme } from '@mui/material/styles';
import { useSearch } from '@tanstack/react-router';
import type React from 'react';
import { NAV_BAR_HEIGHT } from 'src/constants';
import { useFilterPopover } from 'src/context/FilterPopoverContext';
import FilterWidget from 'src/HomePage/DrawerAndPushedContents/PushedContents/TopFilterNavBar/FilterWidget/FilterWidget';
import SearchAppBar from 'src/HomePage/DrawerAndPushedContents/PushedContents/TopFilterNavBar/SearchAppBar/SearchAppBar';
import { Divider } from 'src/HomePage/TopNavBar/Divider';
import viewStateVar from 'src/store/viewState';
import type { BaseSearchParams } from 'src/utils/searchSchemas';
import { useFilterChipActions } from './useFilterChipActions';

const CONTENT_PADDING = '0 24px';

interface SearchFilterBarProps {
  /** Optional override for the SearchAppBar placeholder text. */
  placeholder?: string;
  /** Sticky offset from top; defaults to NAV_BAR_HEIGHT. */
  stickyOffset?: number;
  /** When true, uses a semi-transparent background so a banner behind can bleed through. */
  transparentBg?: boolean;
  /** Optional content rendered before the search input (e.g. a collapsed page-title badge). */
  leadingSlot?: React.ReactNode;
}

const SearchFilterBar: React.FC<SearchFilterBarProps> = ({ placeholder, stickyOffset, transparentBg, leadingSlot }) => {
  const theme = useTheme();
  const search = useSearch({ strict: false }) as BaseSearchParams;
  const { isOpen, anchorPosition, open: openFilter, close: closeFilter } = useFilterPopover();
  const { filterChips, viewModels } = useReactiveVar(viewStateVar);
  const { handleDeleteChip, handleClearAll } = useFilterChipActions();

  const chips = filterChips as Array<{ choice: string; category: string; chip: string }>;
  const hasChips = chips.length > 0;
  const labelsReady = viewModels?.library?.ViewFilterBarFirstDropdown != null;
  const urlFilters = search.filters || [];
  const hasUrlFilters = urlFilters.length > 0;

  const backgroundColor = transparentBg
    ? alpha(theme.palette.background.default, 0.35)
    : theme.palette.background.default;
  const backdropFilter = transparentBg ? 'blur(16px)' : undefined;

  return (
    <div
      style={{
        position: 'sticky',
        top: stickyOffset ?? NAV_BAR_HEIGHT,
        zIndex: 9,
        backgroundColor,
        backdropFilter,
        WebkitBackdropFilter: backdropFilter,
      }}
    >
      <div
        style={{
          padding: CONTENT_PADDING,
          paddingTop: 8,
          paddingBottom: 4,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {/* Optional leading content (e.g. collapsed gallery title when hero scrolls out) */}
        {leadingSlot}

        {/* Search bar — constrained width */}
        <div style={{ flex: '0 1 610px', minWidth: 280 }}>
          <SearchAppBar placeholder={placeholder} />
        </div>

        {/* Filters dropdown */}
        <Button
          variant="outlined"
          endIcon={<ArrowDropDownIcon />}
          onClick={(e) => openFilter(e.currentTarget)}
          sx={{
            textTransform: 'none',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            borderRadius: '4px',
            fontSize: '0.8125rem',
            height: 38,
            padding: '6px 14px',
            color: (t: Theme) => alpha(t.palette.text.primary, 0.7),
            backgroundColor: (t: Theme) => alpha(t.palette.common.white, 0.08),
            borderColor: (t: Theme) => alpha(t.palette.text.primary, 0.3),
            '&:hover': {
              borderColor: (t: Theme) => alpha(t.palette.text.primary, 0.3),
              backgroundColor: (t: Theme) => alpha(t.palette.common.white, 0.06),
              color: 'text.primary',
            },
            '& .MuiButton-endIcon': {
              marginLeft: '2px',
              color: (t: Theme) => alpha(t.palette.text.primary, 0.6),
            },
            '& .MuiButton-endIcon > *:nth-of-type(1)': { fontSize: '20px' },
          }}
        >
          Filters
        </Button>

        {/* Inline filter chips */}
        {!labelsReady && hasUrlFilters
          ? urlFilters.map((_, i) => (
              <Skeleton
                // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder skeletons
                key={`skel-${i}`}
                variant="rounded"
                width={100}
                height={26}
                sx={{ borderRadius: '12px', flexShrink: 0 }}
              />
            ))
          : chips.map((chip, index) => (
              <Chip
                // biome-ignore lint/suspicious/noArrayIndexKey: filter chips may have duplicate values
                key={index}
                label={chip.chip}
                size="small"
                onDelete={() => handleDeleteChip(index)}
                sx={{
                  backgroundColor: (t: Theme) => alpha(t.palette.secondary.main, 0.12),
                  color: 'secondary.main',
                  border: (t: Theme) => `1px solid ${alpha(t.palette.secondary.main, 0.2)}`,
                  borderRadius: '12px',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  height: 26,
                  '& .MuiChip-deleteIcon': {
                    color: (t: Theme) => alpha(t.palette.secondary.main, 0.5),
                    fontSize: 14,
                    '&:hover': { color: 'secondary.main' },
                  },
                }}
              />
            ))}

        {/* Clear all */}
        {hasChips && (
          <Box
            component="button"
            type="button"
            onClick={handleClearAll}
            sx={{
              color: 'secondary.main',
              fontSize: '0.75rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              padding: '2px 8px',
              borderRadius: 1,
              background: 'none',
              border: 'none',
              '&:hover': {
                backgroundColor: (t: Theme) => alpha(t.palette.secondary.main, 0.08),
              },
              '&:focus-visible': {
                outline: (t: Theme) => `2px solid ${alpha(t.palette.secondary.main, 0.45)}`,
                outlineOffset: 1,
              },
            }}
          >
            Clear all
          </Box>
        )}
      </div>

      {/* Keep FilterWidget mounted so its URL→chips effect runs as labels and filters arrive — unmounting would drop URL-rehydration on first paint. */}
      <FilterWidget
        placement="topSideFilter"
        filterPopoverOpen={isOpen}
        filterAnchorPosition={anchorPosition}
        onCloseFilterPopover={closeFilter}
      />

      <div style={{ padding: CONTENT_PADDING }}>
        <Divider />
      </div>
    </div>
  );
};

export default SearchFilterBar;
